import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { OfflineBanner } from '../../components/lesson/OfflineBanner.js';
import { ApiError } from '../../lib/api.js';
import {
  approveLesson,
  fetchLessonApprovals,
  sendBackLesson,
  type Lesson,
} from '../../lib/lesson-api.js';
import { useOutbox } from '../../lib/offline/useOutbox.js';
import { t, formatDateTime } from '../../lib/i18n.js';

/**
 * The Approver inbox (spec §10 key screens).
 *
 * Approving is a desk activity, not a field one, so this screen does not queue
 * anything offline. That is deliberate rather than an omission: an approval
 * carries the approver's name and signature onto a quality record, and queueing
 * one on a device would mean a record could show as approved locally while the
 * server still holds it pending — a discrepancy in exactly the field an auditor
 * checks first.
 *
 * Send-back remarks are mandatory, enforced by the API as well as here. An
 * author told only "rejected" learns nothing and resubmits the same record.
 */
export function LessonApprovals() {
  const outbox = useOutbox();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);

  const approvals = useQuery({
    queryKey: ['lesson-approvals'],
    queryFn: fetchLessonApprovals,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['lesson-approvals'] });
    void queryClient.invalidateQueries({ queryKey: ['lessons'] });
  };

  const approve = useMutation({
    mutationFn: (id: string) => approveLesson(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : t("That could not be approved.")),
  });

  const sendBack = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => sendBackLesson(id, text),
    onSuccess: () => {
      setError(null);
      setOpenId(null);
      setRemarks('');
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : t("That could not be sent back.")),
  });

  const pending: Lesson[] = approvals.data?.pending ?? [];

  return (
    <div className="min-h-full">
      <AppTopBar app="lesson-learned" />
      <OfflineBanner state={outbox} />
      <Breadcrumbs app="lesson-learned" trail={['Approvals']} />

      <main className="mx-auto max-w-5xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Lessons awaiting approval")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Only the lessons your scope reaches are listed.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}

        {approvals.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {!approvals.isLoading && pending.length === 0 && (
          <p className="mt-6 rounded-md border border-border bg-surface p-6 text-content-secondary">
            {t("Nothing is waiting on you.")}
          </p>
        )}

        <ul className="mt-6 grid list-none gap-4 p-0">
          {pending.map((lesson) => (
            <li key={lesson.id} className="rounded-md border border-border bg-surface p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-base font-semibold">
                  <Link to={`/lesson-learned/lessons/${lesson.id}`} className="no-underline">
                    {lesson.referenceNo}
                  </Link>{' '}
                  — {lesson.title}
                </h2>
                <span className="text-sm capitalize text-content-secondary">
                  {lesson.issueCategory} · {lesson.impact}
                </span>
              </div>

              <p className="mt-1 text-sm text-content-secondary">
                {t("Submitted by")}{' '}{lesson.submitterName ?? t("an unnamed user")}
                {lesson.submitterDesignation ? `, ${lesson.submitterDesignation}` : ''}
                {lesson.submittedAt
                  ? t(" on {0}", { 0: formatDateTime(lesson.submittedAt) })
                  : ''}
              </p>

              {lesson.description && (
                <p className="mt-3 whitespace-pre-wrap text-sm">{lesson.description}</p>
              )}

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => approve.mutate(lesson.id)}
                  disabled={approve.isPending}
                  className="rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse disabled:opacity-50"
                >
                  {t("Approve")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpenId(openId === lesson.id ? null : lesson.id);
                    setRemarks('');
                  }}
                  className="rounded-sm border border-border-strong px-4 py-2 text-sm font-medium"
                >
                  {t("Send back")}
                </button>
              </div>

              {openId === lesson.id && (
                <div className="mt-4 rounded-sm border border-border p-3">
                  <label htmlFor={`remarks-${lesson.id}`} className="text-sm font-medium">
                    {t("What needs changing?")}
                  </label>
                  <p id={`remarks-hint-${lesson.id}`} className="mt-1 text-xs text-content-secondary">
                    {t("Required. This is what the author will see, so it has to be enough to act on.")}
                  </p>
                  <textarea
                    id={`remarks-${lesson.id}`}
                    aria-describedby={`remarks-hint-${lesson.id}`}
                    value={remarks}
                    onChange={(event) => setRemarks(event.target.value)}
                    rows={3}
                    className="mt-2 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => sendBack.mutate({ id: lesson.id, text: remarks })}
                    disabled={remarks.trim().length < 10 || sendBack.isPending}
                    className="mt-2 rounded-sm border border-border-strong px-4 py-2 text-sm font-medium disabled:opacity-50"
                  >
                    {sendBack.isPending ? t("Sending back…") : t("Confirm send back")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
